import { useCallback, useEffect, useMemo, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
// Import Types
import type { FilterChip, FilterTagGroup, FilterTagRef, FilterTagsContextValue } from 'types';
import type { FilterHolderRef } from '../FilterHolder/FilterHolder';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
// Import Services
import { UtilsHttpService } from 'services/http';
import { mapToTagListModel, resetFilterFields, createAddFilterTagDTO } from './service';
// Import Constants
import { l, AlertTypes, GACategoryEvents } from 'constants/common';

const { error, success } = AlertTypes;
const { GAFiltersCategory } = GACategoryEvents;

export interface UseFilterTagsParams {
    filtersTagKey: string;
    filterHolderRef: RefObject<FilterHolderRef>;
    isFilterHolderDisabled?: boolean;
    /** Opens / closes the filter drawer. Only needed while an "Add Filters" entry point is rendered. */
    filterHolderStateSetter?: (isHolderOpened: boolean) => void;
}

export interface UseFilterTagsReturnValue {
    chips: FilterChip[];
    filtersTagsList: FilterTagGroup[];
    isFiltersTagListLoading: boolean;
    isAddFilterLoading: boolean;
    isVisibleSaveFilterTagModal: boolean;
    isVisibleConfigureFilterTagModal: boolean;
    filterName: Nilable<string>;
    setFilterName: Dispatch<SetStateAction<Nilable<string>>>;
    changeFiltersList: FilterTagRef['changeFiltersList'];
    getFiltersTagsList: () => void;
    applyFiltersToFilterHolderAndFilterTag: (existingFilters?: FilterChip[]) => void;
    handleAddFiltersClick: () => void;
    handleSaveFiltersClick: () => void;
    handleSavedFiltersClick: () => void;
    handleSaveModalCancel: () => void;
    handleConfigureModalCancel: () => void;
    handleSaveFilter: () => void;
    handleChipRemove: (index: number) => void;
    contextValue: FilterTagsContextValue;
}

/**
 * Owns the active filter chips of a list page and the "Save filters" / "Saved filters" flows
 * (requests, modals, GA events). Shared by the legacy `FilterTag` card and by `FilterTagsProvider`.
 */
export const useFilterTags = ({
    filtersTagKey,
    filterHolderRef,
    isFilterHolderDisabled = false,
    filterHolderStateSetter,
}: UseFilterTagsParams): UseFilterTagsReturnValue => {
    const { t } = useTranslation();
    const { doPostRequest, doGetRequest } = useRequest();
    const { showToaster } = useToaster();
    const { logEvent } = useGTMEvent();
    const { partnerDateFormat } = usePartnerDateFormats();

    const [chips, setChips] = useState<FilterChip[]>([]);
    const [filtersTagsList, setFiltersTagsList] = useState<FilterTagGroup[]>([]);
    const [isVisibleSaveFilterTagModal, setIsVisibleSaveFilterTagModal] = useState(false);
    const [isVisibleConfigureFilterTagModal, setIsVisibleConfigureFilterTagModal] = useState(false);
    const [isAddFilterLoading, setIsAddFilterLoading] = useState(false);
    const [isFiltersTagListLoading, setIsFiltersTagListLoading] = useState(false);
    const [filterName, setFilterName] = useState<Nilable<string>>();

    const { getFiltersTagGroup, addFiltersTagGroup } = useCancelableRequests(
        {
            getFiltersTagGroup: UtilsHttpService.getFiltersTagGroup,
            addFiltersTagGroup: UtilsHttpService.addFiltersTagGroup,
        },
        { takeLatest: ['getFiltersTagGroup'] },
    );

    const getFiltersTagsList = () => {
        setIsFiltersTagListLoading(true);
        doGetRequest(getFiltersTagGroup.request, {
            queryString: { filtersTagGroupName: filtersTagKey },
            successCallback: ({ Value }) => {
                if (!isNil(Value)) {
                    setFiltersTagsList(Value);
                }
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsFiltersTagListLoading(false);
            }
        });
    };

    const changeFiltersList = useCallback(
        (filtersState?: Record<string, unknown>[]) => {
            setChips(mapToTagListModel(filtersState, partnerDateFormat));
        },
        [partnerDateFormat],
    );

    const applyFiltersToFilterHolderAndFilterTag = useCallback(
        (existingFilters: FilterChip[] = []) => {
            filterHolderRef.current?.changeAndApplyFilters(
                resetFilterFields(filterHolderRef.current?.filtersState, existingFilters),
            );
            setIsVisibleConfigureFilterTagModal(false);
        },
        [filterHolderRef],
    );

    const handleChipRemove = useCallback(
        (removedIndex: number) => {
            applyFiltersToFilterHolderAndFilterTag(chips.filter((_chip, index) => index !== removedIndex));
        },
        [applyFiltersToFilterHolderAndFilterTag, chips],
    );

    const handleAddFiltersClick = useCallback(() => {
        logEvent(GAFiltersCategory.name, GAFiltersCategory.events[`${filtersTagKey}AddButton`], filtersTagKey, 1);
        filterHolderStateSetter?.(true);
    }, [filterHolderStateSetter, filtersTagKey, logEvent]);

    const handleSaveFiltersClick = useCallback(() => {
        logEvent(GAFiltersCategory.name, GAFiltersCategory.events[`${filtersTagKey}SaveButton`], filtersTagKey, 1);
        setIsVisibleSaveFilterTagModal(true);
    }, [filtersTagKey, logEvent]);

    const handleSavedFiltersClick = useCallback(() => {
        logEvent(GAFiltersCategory.name, GAFiltersCategory.events[`${filtersTagKey}SavedButton`], filtersTagKey, 1);
        setIsVisibleConfigureFilterTagModal(true);
    }, [filtersTagKey, logEvent]);

    const handleSaveModalCancel = () => {
        setIsVisibleSaveFilterTagModal(false);
        setFilterName(undefined);
    };

    const handleConfigureModalCancel = () => {
        setIsVisibleConfigureFilterTagModal(false);
    };

    const handleSaveFilter = () => {
        setIsAddFilterLoading(true);
        doPostRequest(addFiltersTagGroup.request, {
            requestBody: createAddFilterTagDTO(filtersTagKey, filterName, chips),
            successCallback: () => {
                handleSaveModalCancel();
                showToaster(success, t(l.FilterGroupWasSavedSuccessfully));
                getFiltersTagsList();
            },
            errorCallback: (errorMessage) => {
                showToaster(error, errorMessage ?? '');
            },
        }).then(() => {
            setIsAddFilterLoading(false);
        });
    };

    const init = () => {
        getFiltersTagsList();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    const contextValue = useMemo<FilterTagsContextValue>(
        () => ({
            filtersTagKey,
            chips,
            isDisabled: isFilterHolderDisabled,
            isSaveDisabled: isEmpty(chips) || isFilterHolderDisabled,
            removeChip: handleChipRemove,
            openSaveFiltersModal: handleSaveFiltersClick,
            openSavedFiltersModal: handleSavedFiltersClick,
            openFilterHolder: handleAddFiltersClick,
        }),
        [
            filtersTagKey,
            chips,
            isFilterHolderDisabled,
            handleChipRemove,
            handleSaveFiltersClick,
            handleSavedFiltersClick,
            handleAddFiltersClick,
        ],
    );

    return {
        chips,
        filtersTagsList,
        isFiltersTagListLoading,
        isAddFilterLoading,
        isVisibleSaveFilterTagModal,
        isVisibleConfigureFilterTagModal,
        filterName,
        setFilterName,
        changeFiltersList,
        getFiltersTagsList,
        applyFiltersToFilterHolderAndFilterTag,
        handleAddFiltersClick,
        handleSaveFiltersClick,
        handleSavedFiltersClick,
        handleSaveModalCancel,
        handleConfigureModalCancel,
        handleSaveFilter,
        handleChipRemove,
        contextValue,
    };
};
